// React and MobX
import React, { useContext, useState } from "react";
import { useObserver } from "mobx-react-lite";

// Material-UI Core and Styles
import { Box, Card, CardActionArea, CardContent, CardMedia, Drawer, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";

// Core
import { useRouter } from "Core/Utils";
import { Loader } from "Core/Components/Loader";

// Custom Components
import { Error } from "Custom/Components/Error";
import { PrivateBlob } from "Custom/Components/Image/PrivateImage";
import { Stores, StoresContext } from "Custom/Stores";

// Custom Views
import { ChoicesSummary } from "./ChoicesSummary";
import { ChoicesArea } from "./ChoicesArea";

// Custom Styles
import { DashboardPageStyles, MainPageStyles, usePageAnimationStyles } from "Custom/StylesAppSpecific/DashboardPagesStyling";
import { ChoicesPageStyles } from "Custom/StylesAppSpecific/DashboardSubViews/ChoicesPageStyling";

// Custom ViewModels
import { ChoicesViewModel } from "Custom/ViewModels/DashboardSubViewModels/ChoicesViewModel";

// Enums and Models
import { ApiStatus } from "Custom/Models/Enums/ApiStatus";
import { ChoicesSummaryViewModel } from "Custom/ViewModels/DashboardSubViewModels/ChoicesSummaryViewModel";

export const Choices: React.FC = () => {
    // #region Code Behind
    const router = useRouter();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md")) ? true : false;
    const stores = useContext<Stores>(StoresContext);
    const portalThemeStore = stores.domain.PortalThemeStore;

    const propertyId = (router.match.params as any).propertyId;
 
    const dashboardPageStyles = DashboardPageStyles();
    const choicesPageStyles = ChoicesPageStyles();
    const pageAnimationStyles = usePageAnimationStyles();
    const mainPageStyles = MainPageStyles();

    const [viewModel] = useState(() => new ChoicesViewModel(propertyId));
    const [viewModelSummary] = useState(() => new ChoicesSummaryViewModel(propertyId));
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedArea, setSelectedArea] = useState<string | null>(null);

    const onChoicesAreaClick = (areaId: string) => {
        if (isDesktop) {
            viewModel.navigateToChoicesArea(propertyId, areaId); // Navigate for desktop
        } else {
            setSelectedArea(areaId);
            setDrawerOpen(true);
        }
    };

    const closeDrawer = () => {
        setDrawerOpen(false);
        setSelectedArea(null);
    };
    // #endregion Code Behind

    const renderBusy = () => <Loader loadingText="Fetching choices..." />;

    const renderError = () => <Error errorText="Failed to retrieve the choices." />;

    const renderIntroductionRow = () => {
        const introductionText = () => {
            if (viewModel.isChoicesLocked) {
                return (
                    <>
                        <Typography className={choicesPageStyles.introductionTitle}>Your choices</Typography>

                        <Typography className={choicesPageStyles.introductionSubTitle}>
                            Currently, you can only view your choices. Please contact your sales agent if you need to make any changes.
                        </Typography>
                    </>
                );
            } else {
                return (
                    <>
                        <Typography component="h2" className={choicesPageStyles.introductionTitle}>
                            Choose how you want your house
                        </Typography>
                        <Typography className={choicesPageStyles.introductionSubTitle}>Select an area of the house to select your options and choose your upgrades.</Typography>
                    </>
                );
            }
        };

        return (
            <Box
                className={choicesPageStyles.introductionHeader}
                style={{
                    ...(portalThemeStore.primaryColour ? { backgroundColor: portalThemeStore.primaryColour } : {}),
                }}
            >
                <Box>{introductionText()}</Box>
            </Box>
        );
    };

    const renderChoicesAreas = () => (
        <Box className={choicesPageStyles.choicesAreas}>
            <Box>
                {viewModel.ChoicesAreas.map((choicesArea, index) => (
                    <Card className={choicesPageStyles.choicesArea} key={index}>
                        <CardActionArea onClick={() => onChoicesAreaClick(choicesArea.id)}>
                            <PrivateBlob src={choicesArea.areaImage} title="" cardMedia={true} />
                            <CardContent>
                                <Typography className={choicesPageStyles.choicesAreaTitle}>
                                    <b>{choicesArea.areaName}</b>
                                </Typography>
                                <Typography className={choicesPageStyles.choicesAreaSubTitle}>
                                    <b>{`${choicesArea.productGroupsUsed} of ${choicesArea.productGroupsAvailable}`}</b> choices made
                                </Typography>
                            </CardContent>
                        </CardActionArea>
                    </Card>
                ))}
            </Box>
        </Box>
    );

    const renderChoicesSummary = () => (
        <Box className={choicesPageStyles.choicesAreasSummaryContainer}>
            <Box>
                <ChoicesSummary canEditChoices={!viewModel.isChoicesLocked} propertyId={propertyId} viewModelSummary={viewModelSummary} stores={stores} />
            </Box>
        </Box>
    );

    return useObserver(() => (
        <Box className={`${dashboardPageStyles.root} ${choicesPageStyles.root} ${pageAnimationStyles.pageWrapper}`}>
            <Box className={mainPageStyles.workspace}>
                {viewModel.ApiStatus === ApiStatus.success && (
                    <React.Fragment>
                        {renderIntroductionRow()}
                        {renderChoicesAreas()}
                        {renderChoicesSummary()}
                    </React.Fragment>
                )}
                {viewModel.ApiStatus === ApiStatus.loading && <React.Fragment>{renderBusy()}</React.Fragment>}
                {viewModel.ApiStatus === ApiStatus.error && <React.Fragment>{renderError()}</React.Fragment>}
            </Box>

            {/* Drawer for mobile */}
            <Drawer anchor="bottom" open={drawerOpen} onClose={closeDrawer} className="choicesPageMobileDrawer">
                {selectedArea && <ChoicesArea propertyId={propertyId} areaId={selectedArea} onClose={closeDrawer} stores={stores} />}
            </Drawer>
        </Box>
    ));
};
